import { getLocation } from "connected-react-router";
import { Store } from "redux";

import type { TourStepAction } from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import i18nService from "@app/config/I18nService";
import Path from "@app/enum/Path";
import ViewMode from "@app/scene/deal/enum/ViewMode";
import { switchView } from "@app/scene/deal/store/actions";
import { getFunnels } from "@app/store/deal";
import type { Actions, RootState } from "@app/store/rootReducer";

import { errorMessages } from "./messages";

export const TARGET_FUNNEL_NAME = "North America - Referral Initiative Funnel";
export const TARGET_LAYOUT_NAME = "North America - Referral Initiative and Dentist Layout";

// FormLayoutSelector has no data-* attributes; the label span shows the active layout name
// (the modal's active layout is component state, so the DOM is the only place to read it from)
const LAYOUT_SELECTOR_LABEL = ".ant-modal [class*=FormLayoutSelector_label]";
const LAYOUT_SELECTOR_TRIGGER = ".ant-modal [class*=FormLayoutSelector_button]";
// the only app-authored data-* attribute in the Create Deal modal body — proves the form rendered
const MODAL_FORM_MARKER = ".ant-modal [data-id='js-funnel-staging-field']";
// layout dropdown items carry the layout id, not the name, so items are matched by text
const OPEN_DROPDOWN_ITEM = ".ant-dropdown:not(.ant-dropdown-hidden) .ant-dropdown-menu-item";

type TourStore = Store<RootState, Actions>;

export const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

// resolves false instead of hanging when the element never appears (default 10s cap)
export const waitFor = (selector: string, timeout?: number) =>
  new Promise<boolean>((resolve) => {
    waitForElement(selector, () => resolve(true), {
      failureCallback: () => resolve(false),
      timeout,
    });
  });

export const clickElement = (selector: string) =>
  document.querySelector<HTMLElement>(selector)?.click();

// Funnels are fetched during app initialization (store/app/sagas dispatches fetchAllFunnels
// and waits for it), so by the time a tour can run the slice is populated — safe to check directly.
export const checkTargetFunnelExistsOnBefore = async ({
  store,
}: {
  store: TourStore;
}): Promise<TourStepAction | void> => {
  const exists = getFunnels(store.getState()).some((funnel) => funnel.name === TARGET_FUNNEL_NAME);
  if (!exists) {
    return {
      action: "error" as const,
      message: i18nService.formatMessage(errorMessages.noTargetFunnel),
    };
  }
};

// Ensure the Create Deal modal uses the target layout. Not a tour step: silently switch when
// possible, silently continue when not (a wrong layout still allows completing the tour).
export const ensureTargetLayoutOnBefore = async (): Promise<void> => {
  await waitFor(MODAL_FORM_MARKER);
  const label = document.querySelector(LAYOUT_SELECTOR_LABEL);
  // no label = the selector isn't rendered (orgs with a single layout) — nothing to switch
  if (!label || label.textContent?.trim() === TARGET_LAYOUT_NAME) {
    return;
  }
  clickElement(LAYOUT_SELECTOR_TRIGGER);
  if (!(await waitFor(OPEN_DROPDOWN_ITEM, 2000))) {
    return;
  }
  const target = Array.from(document.querySelectorAll<HTMLElement>(OPEN_DROPDOWN_ITEM)).find(
    (item) => item.textContent?.trim() === TARGET_LAYOUT_NAME
  );
  if (target) {
    target.click();
  } else {
    // toggle the dropdown back closed and proceed on whatever layout is active
    clickElement(LAYOUT_SELECTOR_TRIGGER);
  }
};

// A step whose target may already be satisfied (e.g. the right funnel preselected) skips ahead.
// The check runs when the step's anchor is found; `selector` must match only in the satisfied state.
export const skipIfPresentOnShow =
  (selector: string, stepId: string) => async (): Promise<TourStepAction | void> => {
    if (document.querySelector(selector)) {
      return { action: "navigate", stepId };
    }
  };

const viewModeFromPathname = (pathname: string): undefined | ViewMode =>
  Object.values(ViewMode).find((mode) => pathname.startsWith(`${Path.DEAL}/${mode}`));

// /deals redirects to the last-used view (localStorage), so the branch decision must wait for
// a view page to actually mount — `toolbarSelector` is present on all three deal views.
export const goToStepForCurrentViewOnBefore =
  (toolbarSelector: string, kanbanStepId: string, otherViewsStepId: string) =>
  async ({ store }: { store: TourStore }): Promise<TourStepAction> => {
    await waitFor(toolbarSelector, 0);
    const { pathname } = getLocation(store.getState());
    return {
      action: "navigate",
      stepId: viewModeFromPathname(pathname) === ViewMode.KANBAN ? kanbanStepId : otherViewsStepId,
    };
  };

// switchView (not a bare route push) — the scene saga migrates the current view's filters
// to the kanban view before pushing /deals/kanban, same as the toggle's own click path
export const switchToKanbanOnAfter = async ({ store }: { store: TourStore }): Promise<void> => {
  const from = viewModeFromPathname(getLocation(store.getState()).pathname);
  if (from && from !== ViewMode.KANBAN) {
    store.dispatch(switchView({ from, to: ViewMode.KANBAN }));
  }
};
